import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ 
    email: "", 
    password: "", 
    full_name: "" 
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, form);
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      toast.success("¡Cuenta creada exitosamente!");
      navigate("/dashboard");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      
      if (status === 429) {
        toast.error("Demasiados intentos. Espera unos minutos.");
      } else if (status === 400 && detail?.includes("email")) {
        toast.error("Este email ya está registrado");
      } else if (!err.response) {
        toast.error("Error de conexión. Verifica tu internet.");
      } else {
        toast.error(detail || "Error al crear cuenta. Intenta de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E11] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1636215096587-21982fbf5843?w=1920&q=80)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Close button to go back to landing */}
      <Link 
        to="/" 
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-[#1E2329] hover:bg-[#2B3139] rounded-full flex items-center justify-center transition-colors"
        data-testid="close-register"
      >
        <X className="w-5 h-5 text-[#848E9C] hover:text-[#EAECEF]" />
      </Link>
      
      <div className="relative w-full max-w-md">
        <div className="card-dark p-6 sm:p-8 space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-[#EAECEF]">Crear Cuenta</h1>
            <p className="text-[#848E9C] text-sm sm:text-base">Empieza a generar ingresos hoy mismo</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Google Register Button */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border-2 border-[#2B3139] hover:border-[#848E9C] bg-[#181A20] hover:bg-[#1E2329] transition-all text-[#EAECEF] font-semibold"
              data-testid="google-register-btn"
            >
              <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Registrarse con Google
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#2B3139]" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-[#181A20] px-3 text-[#848E9C]">o con email</span></div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#EAECEF]">Nombre Completo</Label>
              <Input
                id="name"
                type="text"
                placeholder="Juan Pérez"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="input-dark"
                required
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#EAECEF]">Correo Electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-dark"
                required
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#EAECEF]">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-dark pr-10"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#848E9C] hover:text-[#EAECEF]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full py-6 text-lg font-bold"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              CREAR CUENTA
            </Button>
          </form>

          <div className="text-center">
            <p className="text-[#848E9C]">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-[#FCD535] hover:underline" data-testid="goto-login">
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
